import React, { useEffect, useRef, useState } from 'react';
import { Drawer } from '@mui/material';
import { CaseReportSummary, formatReportDate, isOpeningSession, toRoadSessionNumber } from './sessionReportsUtils';
import SessionReportDetailCard from './SessionReportDetailCard';
import ExpandCollapse from '../common/ExpandCollapse';
import CaseCounselingTrailView from './CaseCounselingTrail';
import Button, { IconButton } from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';
import { t } from '../../utils/translations';

interface SessionReportsTimelineProps {
  summary: CaseReportSummary | null;
  onAddReport?: () => void;
  /** Desktop: embedded panel. Mobile: slide-over drawer. */
  variant?: 'panel' | 'drawer';
  open?: boolean;
  onClose?: () => void;
  /** Deep link from the dashboard road: road session number to expand. */
  focusSession?: number | null;
  onFocusApplied?: () => void;
}

const SessionReportsTimeline: React.FC<SessionReportsTimelineProps> = ({
  summary,
  onAddReport,
  variant = 'panel',
  open = false,
  onClose,
  focusSession = null,
  onFocusApplied,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const isDrawer = variant === 'drawer';
  const sessionNumbers = summary?.reports.map((report) => report.sessionNumber) ?? [];
  const reportRefs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (focusSession === null || !summary) return;
    const numbers = summary.reports.map((report) => report.sessionNumber);
    const target = summary.reports.find(
      (report) => toRoadSessionNumber(report.sessionNumber, numbers) === focusSession
    );
    if (!target) return;

    setExpandedId(target.id);
    onFocusApplied?.();
    window.requestAnimationFrame(() => {
      reportRefs.current[target.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [focusSession, summary, onFocusApplied]);

  const content = !summary ? (
    <div className="flex flex-1 items-center justify-center p-8 text-center">
      <p className="text-sm text-slate-500">{t.sessionReports.selectCase}</p>
    </div>
  ) : (
    <>
      <div className="shrink-0 border-b border-slate-200 px-4 pb-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              {summary.case.counseledName}
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">{summary.case.title}</p>
            <p className="mt-1 text-xs text-slate-500">
              {summary.reportCount}{' '}
              {summary.reportCount === 1
                ? t.sessionReports.reportSingular
                : t.sessionReports.reportPlural}{' '}
              · {t.sessionReports.sessions.toLowerCase()}
            </p>
            <CaseCounselingTrailView
              reports={summary.reports}
              variant="compact"
              className="mt-2"
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onAddReport && (
              <Button size="sm" variant="primary" onClick={onAddReport}>
                {t.sessionReports.addReport}
              </Button>
            )}
            {isDrawer && onClose && (
            <IconButton
              icon={actionIcon.close}
              size="md"
              label={t.common.close}
              onClick={onClose}
            />
            )}
          </div>
        </div>
      </div>

      <div
        className={
          isDrawer
            ? 'flex-1 space-y-3 overflow-auto px-4 py-4'
            : 'space-y-3 px-4 py-4'
        }
      >
        {summary.case.closure && (
          <section className="rounded-xl border border-brand-200 bg-brand-50/60 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-brand-900">
                  {t.caseClosure.title}
                </p>
                <p className="mt-0.5 text-xs text-brand-700">
                  {t.caseClosure.closedOn}:{' '}
                  {summary.case.closure.closedAt.toLocaleDateString('ro-RO')}
                  {summary.case.closure.createdByName
                    ? ` · ${t.caseClosure.closedBy}: ${summary.case.closure.createdByName}`
                    : ''}
                </p>
              </div>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-brand-700">
                {t.caseClosure.outcome[summary.case.closure.status]}
              </span>
            </div>

            <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
              {summary.case.closure.status === 'unfinished' && (
                <>
                  {summary.case.closure.unfinishedReason && (
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium text-slate-500">
                        {t.caseClosure.unfinishedReason}
                      </dt>
                      <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                        {summary.case.closure.unfinishedReason}
                      </dd>
                    </div>
                  )}
                  {summary.case.closure.nextSteps && (
                    <div className="sm:col-span-2">
                      <dt className="text-xs font-medium text-slate-500">
                        {t.caseClosure.nextSteps}
                      </dt>
                      <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                        {summary.case.closure.nextSteps}
                      </dd>
                    </div>
                  )}
                </>
              )}
              {summary.case.closure.status === 'cancelled' &&
                summary.case.closure.cancellationReason && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-medium text-slate-500">
                      {t.caseClosure.cancellationReason}
                    </dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                      {summary.case.closure.cancellationReason}
                    </dd>
                  </div>
                )}
              {summary.case.closure.status === 'finished' && (
                <>
              {summary.case.closure.problemUnderstanding && (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium text-slate-500">
                    {t.caseClosure.problemUnderstanding}
                  </dt>
                  <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                    {summary.case.closure.problemUnderstanding}
                  </dd>
                </div>
              )}
              {summary.case.closure.observedChanges && (
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {t.caseClosure.observedChanges}
                  </dt>
                  <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                    {summary.case.closure.observedChanges}
                  </dd>
                </div>
              )}
              {summary.case.closure.gospelRelationship && (
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {t.caseClosure.gospelRelationship}
                  </dt>
                  <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                    {summary.case.closure.gospelRelationship}
                  </dd>
                </div>
              )}
              {summary.case.closure.finalEvaluation && (
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {t.caseClosure.finalEvaluation}
                  </dt>
                  <dd className="mt-0.5 text-slate-800">
                    {t.caseClosure.evaluation[summary.case.closure.finalEvaluation]}
                  </dd>
                </div>
              )}
              {summary.case.closure.evaluationDetails && (
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {t.caseClosure.evaluationDetails}
                  </dt>
                  <dd className="mt-0.5 whitespace-pre-wrap text-slate-800">
                    {summary.case.closure.evaluationDetails}
                  </dd>
                </div>
              )}
                </>
              )}
            </dl>
          </section>
        )}

        {summary.reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-10 text-center">
            <p className="text-sm text-slate-500">{t.sessionReports.addFirstReportHint}</p>
          </div>
        ) : (
          <div className="relative space-y-4">
            <div
              className="absolute bottom-0 left-[5px] top-2 w-px bg-slate-200"
              aria-hidden="true"
            />

            {summary.reports.map((report) => {
              const isExpanded = expandedId === report.id;

              return (
                <article
                  key={report.id}
                  ref={(element) => {
                    reportRefs.current[report.id] = element;
                  }}
                  className="relative pl-8"
                >
                  <div
                    className="absolute left-0 top-2 h-2.5 w-2.5 rounded-full bg-brand-500 ring-4 ring-white"
                    aria-hidden="true"
                  />

                  <div className="rounded-xl border border-slate-200 bg-slate-50/50 transition hover:border-slate-300 hover:bg-white">
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : report.id)}
                      aria-expanded={isExpanded}
                      className="block w-full px-4 pb-2 pt-4 text-left"
                    >
                      <p className="text-sm font-semibold text-slate-900">
                        {t.sessionReports.sessionNumber}{' '}
                        {toRoadSessionNumber(report.sessionNumber, sessionNumbers)}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {formatReportDate(report.createdAt)} — {report.createdByName}
                      </p>
                    </button>

                    <ExpandCollapse open={!isExpanded}>
                      <p className="px-4 pb-4 text-sm text-slate-600">
                        <span className="font-medium text-slate-700">
                          {t.sessionReports.theme}:
                        </span>{' '}
                        {report.mainTheme}
                      </p>
                    </ExpandCollapse>

                    <ExpandCollapse open={isExpanded}>
                      <SessionReportDetailCard
                        report={report}
                        className="px-4 pb-4"
                        isOpeningSession={isOpeningSession(report.sessionNumber, sessionNumbers)}
                      />
                    </ExpandCollapse>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );

  if (isDrawer) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 480,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#fff',
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <aside className="flex min-h-[28rem] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {content}
    </aside>
  );
};

export default SessionReportsTimeline;
